/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import { createRouter, createWebHistory } from 'vue-router/auto';
import { setupLayouts } from 'virtual:generated-layouts';
import { usePostHog } from '@/composables/usePostHog';

/**
 * Normalisation des slashs DOUBLÉS avant la création du routeur. Un hôte qui
 * concatène une base finissant par « / » à « /variants/... » produit
 * « //variants/... » : aucune route ne matche et l'app rend une page BLANCHE.
 * On réécrit l'URL (sans rechargement) pour que le routeur parte d'un chemin sain.
 */
(function collapseDuplicateSlashes() {
    try {
        const { pathname, search, hash } = window.location;
        const clean = pathname.replace(/\/{2,}/g, '/');
        if (clean !== pathname) {
            window.history.replaceState(window.history.state, '', `${clean}${search}${hash}`);
        }
    } catch {
        /* best-effort */
    }
})();

const router = createRouter({
    history: createWebHistory(window.env.BASE_URL),
    extendRoutes: setupLayouts
});

/**
 * Mesure d'usage : initialisée ICI (et pas dans `main.ts`) pour que la capture
 * automatique des vues de page couvre la navigation interne de l'app dès la
 * première route. Sans clé configurée, l'appel est un no-op.
 */
usePostHog();

/**
 * Auto-récupération après REDÉPLOIEMENT : les hash des chunks changent à chaque
 * build. Un onglet ouvert garde en cache un `index.html` qui pointe vers des
 * modules désormais absents → « Failed to fetch dynamically imported module »
 * (404) et la navigation casse. On recharge UNE fois vers la cible pour récupérer
 * le nouvel index (garde anti-boucle : au plus un rechargement / 10 s par session).
 */
router.onError((error, to) => {
    const msg = String((error as { message?: string })?.message || '');
    const chunkFail =
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg) ||
        /error loading dynamically imported module/i.test(msg);
    if (!chunkFail) {
        return;
    }
    try {
        const key = 'pxl:chunk-reload';
        const last = Number(sessionStorage.getItem(key) || 0);
        if (Date.now() - last > 10000) {
            sessionStorage.setItem(key, String(Date.now()));
            // Sauve le brouillon (sync) AVANT de recharger la nouvelle version ET
            // autorise sa restauration : la mise à jour de l'app est le SEUL cas
            // où le brouillon doit revenir (une navigation ordinaire en laisse un
            // derrière elle aussi, cf. useCanvasAutosave). Via window (enregistré
            // par useCanvasAutosave) pour ne PAS importer Studio ici — il accède à
            // des stores Pinia au chargement du module.
            try {
                (window as any).__pxlMarkAppUpdate?.();
            } catch {
                /* best-effort */
            }
            window.location.assign(to?.fullPath || window.location.href);
        }
    } catch {
        // Repli (sessionStorage indisponible) : même situation de mise à jour,
        // donc même droit de restauration. L'appel est déjà défensif en interne.
        try {
            (window as any).__pxlMarkAppUpdate?.();
        } catch {
            /* best-effort */
        }
        window.location.reload();
    }
});

export default router;
